// @ts-check
/** @typedef {import('../../types/api').TaskComment} TaskComment */
/** @typedef {import('../../types/api').TaskAttachment} TaskAttachment */

import { format } from 'date-fns';
import { Calendar, Clock, MessageSquare, Paperclip, Tag, User, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import taskService from '../../services/taskService';
import Modal from '../ui/Modal';
import TaskAttachmentItem from './TaskAttachmentItem';
import TaskCommentItem from './TaskCommentItem';
import TaskPriorityBadge from './TaskPriorityBadge';
import TaskStatusBadge from './TaskStatusBadge';

const TaskDetailModal = ({ task, onClose, onUpdate, adminUsers = [], currentUser }) => {
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'comments', 'attachments'
  /** @type {[TaskComment[], Function]} */
  const [comments, setComments] = useState([]);
  /** @type {[TaskAttachment[], Function]} */
  const [attachments, setAttachments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  /** @type {[Record<string, any>, Function]} */
  const [formData, setFormData] = useState({});

  const loadComments = useCallback(async () => {
    if (!task) return;
    try {
      const data = await taskService.getComments(task.id);
      setComments(data);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  }, [task]);

  const loadAttachments = useCallback(async () => {
    if (!task) return;
    try {
      const data = await taskService.getAttachments(task.id);
      setAttachments(data);
    } catch (error) {
      console.error('Error loading attachments:', error);
    }
  }, [task]);

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title,
        description: task.description || '',
        assigned_to: task.assigned_to || '',
        due_date: task.due_date || '',
        priority: task.priority,
        status: task.status,
        tags: task.tags ? task.tags.join(', ') : '',
        estimated_hours: task.estimated_hours || '',
        actual_hours: task.actual_hours || '',
      });
      loadComments();
      loadAttachments();
    }
  }, [task, loadComments, loadAttachments]);

  const handleUpdateTask = async () => {
    setIsSubmitting(true);
    try {
      const updateData = {
        title: formData.title,
        description: formData.description || null,
        assigned_to: formData.assigned_to || null,
        due_date: formData.due_date || null,
        priority: formData.priority,
        status: formData.status,
        tags: formData.tags
          ? formData.tags
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean)
          : null,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
        actual_hours: formData.actual_hours ? parseFloat(formData.actual_hours) : null,
      };

      await taskService.updateTask(task.id, updateData);
      setEditMode(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Failed to update task');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await taskService.createComment(task.id, newComment);
      setNewComment('');
      await loadComments();
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateComment = async (commentId, text) => {
    await taskService.updateComment(commentId, text);
    await loadComments();
  };

  const handleDeleteComment = async (commentId) => {
    await taskService.deleteComment(commentId);
    await loadComments();
  };

  const handleAddAttachment = () => {
    const url = prompt('Enter file URL:');
    const fileName = prompt('Enter file name:');

    if (url && fileName) {
      taskService
        .createAttachment(task.id, {
          file_url: url,
          file_name: fileName,
        })
        .then(() => {
          loadAttachments();
        })
        .catch((error) => {
          console.error('Error adding attachment:', error);
          alert('Failed to add attachment');
        });
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    await taskService.deleteAttachment(attachmentId);
    await loadAttachments();
  };

  if (!task) return null;

  return (
    <Modal maxWidth="4xl" onClose={onClose} closeOnBackdrop={false} className="rounded-lg shadow-xl flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center gap-3 flex-1">
          {editMode ? (
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="flex-1 text-xl font-semibold border border-input rounded-lg px-2 py-1"
            />
          ) : (
            <h2 className="text-xl font-semibold text-foreground">{task.title}</h2>
          )}
        </div>
        <div className="flex items-center gap-2">
          {editMode ? (
            <>
              <button
                onClick={handleUpdateTask}
                disabled={isSubmitting}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setEditMode(false)}
                className="px-3 py-1.5 bg-accent text-foreground/80 text-sm rounded-lg hover:bg-muted-foreground/30"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditMode(true)}
              className="px-3 py-1.5 bg-accent text-foreground/80 text-sm rounded-lg hover:bg-muted-foreground/30"
            >
              Edit
            </button>
          )}
          <button onClick={onClose} className="text-muted-foreground/70 hover:text-foreground transition-colors">
            <X size={24} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border overflow-x-auto">
        <button
          onClick={() => setActiveTab('details')}
          className={`flex-shrink-0 whitespace-nowrap px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'details'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Details
        </button>
        <button
          onClick={() => setActiveTab('comments')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'comments'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <div className="flex items-center gap-2">
            <MessageSquare size={16} />
            Comments ({comments.length})
          </div>
        </button>
        <button
          onClick={() => setActiveTab('attachments')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'attachments'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <div className="flex items-center gap-2">
            <Paperclip size={16} />
            Attachments ({attachments.length})
          </div>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'details' && (
          <div className="space-y-6">
            {/* Status & Priority */}
            <div className="flex items-center gap-4">
              {editMode ? (
                <>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="px-3 py-1.5 border border-input rounded-lg text-sm"
                  >
                    <option value="to_do">To Do</option>
                    <option value="in_progress">In Progress</option>
                    <option value="review">Review</option>
                    <option value="done">Done</option>
                    <option value="blocked">Blocked</option>
                  </select>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="px-3 py-1.5 border border-input rounded-lg text-sm"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </>
              ) : (
                <>
                  <TaskStatusBadge status={task.status} />
                  <TaskPriorityBadge priority={task.priority} />
                </>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-2">Description</label>
              {editMode ? (
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-lg"
                  rows={6}
                />
              ) : (
                <p className="text-foreground/80 whitespace-pre-wrap">{task.description || 'No description'}</p>
              )}
            </div>

            {/* Metadata Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Assigned To */}
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-2">
                  <User className="inline mr-1" size={14} /> Assigned To
                </label>
                {editMode ? (
                  <select
                    value={formData.assigned_to}
                    onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm"
                  >
                    <option value="">Unassigned</option>
                    {adminUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.full_name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-foreground/80">{task.assigned_to_name || 'Unassigned'}</p>
                )}
              </div>

              {/* Due Date */}
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-2">
                  <Calendar className="inline mr-1" size={14} /> Due Date
                </label>
                {editMode ? (
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm"
                  />
                ) : (
                  <p className="text-foreground/80">
                    {task.due_date ? format(new Date(task.due_date), 'MMM d, yyyy') : 'No due date'}
                  </p>
                )}
              </div>

              {/* Estimated Hours */}
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-2">
                  <Clock className="inline mr-1" size={14} /> Estimated Hours
                </label>
                {editMode ? (
                  <input
                    type="number"
                    step="0.5"
                    value={formData.estimated_hours}
                    onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })}
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm"
                  />
                ) : (
                  <p className="text-foreground/80">{task.estimated_hours || '-'} hours</p>
                )}
              </div>

              {/* Actual Hours */}
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-2">
                  <Clock className="inline mr-1" size={14} /> Actual Hours
                </label>
                {editMode ? (
                  <input
                    type="number"
                    step="0.5"
                    value={formData.actual_hours}
                    onChange={(e) => setFormData({ ...formData, actual_hours: e.target.value })}
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm"
                  />
                ) : (
                  <p className="text-foreground/80">{task.actual_hours || '-'} hours</p>
                )}
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-2">
                <Tag className="inline mr-1" size={14} /> Tags
              </label>
              {editMode ? (
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="Comma-separated tags"
                  className="w-full px-3 py-2 border border-input rounded-lg text-sm"
                />
              ) : task.tags && task.tags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {task.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-sm rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No tags</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'comments' && (
          <div className="space-y-4">
            {/* Add Comment */}
            <div className="space-y-2">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write a comment..."
                className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                rows={3}
                disabled={isSubmitting}
              />
              <button
                onClick={handleAddComment}
                disabled={isSubmitting || !newComment.trim()}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Adding...' : 'Add Comment'}
              </button>
            </div>

            {/* Comments List */}
            <div className="space-y-3">
              {comments.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No comments yet</p>
              ) : (
                comments.map((comment) => (
                  <TaskCommentItem
                    key={comment.id}
                    comment={comment}
                    currentUserId={currentUser?.id}
                    onUpdate={handleUpdateComment}
                    onDelete={handleDeleteComment}
                  />
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'attachments' && (
          <div className="space-y-4">
            {/* Add Attachment */}
            <button
              onClick={handleAddAttachment}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Paperclip size={16} />
              Add Attachment
            </button>

            {/* Attachments List */}
            <div className="space-y-3">
              {attachments.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No attachments yet</p>
              ) : (
                attachments.map((attachment) => (
                  <TaskAttachmentItem
                    key={attachment.id}
                    attachment={attachment}
                    currentUserId={currentUser?.id}
                    onDelete={handleDeleteAttachment}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TaskDetailModal;
